import { useEffect, useRef, type ReactNode } from "react";
import type { PatientChatConversationTurn } from "../../../lib/patientChat";
import { PromptWorkflowStatus } from "../../noteList/SmartAssistPromptTools";
import type { PromptWorkflowStep, SmartAssistQuickAction } from "../core/types";

// Close enough to the bottom that the user is following along, not reading back.
const PINNED_TO_BOTTOM_PX = 40;

export interface SmartAssistEmptyState {
  title: string;
  body: string;
}

interface SmartAssistConversationProps {
  displayedConversation: PatientChatConversationTurn[];
  reply: string;
  /** What Smart Assist is doing before the first reply text arrives; null when idle. */
  pendingStatus?: string | null;
  shouldShowPromptWorkflowStatus: boolean;
  promptWorkflowStep: PromptWorkflowStep;
  activeQuickAction: SmartAssistQuickAction | null;
  emptyState: SmartAssistEmptyState;
  /** The latest reply card, rendered after the earlier turns. */
  children?: ReactNode;
}

function PendingStatusRow({ status }: { status: string }) {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-w-0 items-center gap-2 text-sm text-light-grey"
    >
      <span
        aria-hidden="true"
        className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-sky-400"
      />
      <span className="min-w-0 break-words [overflow-wrap:anywhere]">{status}</span>
    </div>
  );
}

export default function SmartAssistConversation({
  displayedConversation,
  reply,
  pendingStatus = null,
  shouldShowPromptWorkflowStatus,
  promptWorkflowStep,
  activeQuickAction,
  emptyState,
  children,
}: SmartAssistConversationProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const isPinnedToBottomRef = useRef(true);

  const handleScroll = () => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    isPinnedToBottomRef.current =
      viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight <= PINNED_TO_BOTTOM_PX;
  };

  useEffect(() => {
    if (pendingStatus) isPinnedToBottomRef.current = true;
  }, [pendingStatus]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || !isPinnedToBottomRef.current) return;
    viewport.scrollTop = viewport.scrollHeight;
  }, [displayedConversation, reply, pendingStatus]);

  // Not a live region: streamed text would be read token by token. Completed
  // replies are announced once by useSmartAssist.
  return (
    <div
      ref={viewportRef}
      role="region"
      aria-label="Smart Assist conversation"
      tabIndex={0}
      onScroll={handleScroll}
      className="min-h-0 flex-1 overflow-y-auto bg-gradient-to-b from-white to-slate-50/70 px-4 py-5"
    >
      {shouldShowPromptWorkflowStatus ? (
        <div className="flex h-full min-h-[12rem] items-center justify-center px-4">
          <div className="flex min-w-0 flex-col items-center gap-4">
            <PromptWorkflowStatus step={promptWorkflowStep} quickAction={activeQuickAction} />
            {pendingStatus && <PendingStatusRow status={pendingStatus} />}
          </div>
        </div>
      ) : !displayedConversation.length && !reply && !pendingStatus ? (
        <div className="flex h-full min-h-[12rem] items-center justify-center px-6 text-center">
          <div className="max-w-sm">
            <div className="text-base font-semibold text-accent1">{emptyState.title}</div>
            <p className="mb-0 mt-2 text-sm leading-6 text-light-grey">{emptyState.body}</p>
          </div>
        </div>
      ) : (
        <div className="mx-auto flex min-w-0 w-full max-w-3xl flex-col gap-5">
          {displayedConversation.map((message, index) => (
            <div
              key={`${message.role}-${index}`}
              className={
                message.role === "user" ? "flex min-w-0 justify-end" : "flex min-w-0 justify-start"
              }
            >
              {message.role === "user" ? (
                <div className="max-w-[85%] break-words whitespace-pre-wrap rounded-2xl rounded-br-md bg-slate-100 px-4 py-3 text-sm text-accent1 [overflow-wrap:anywhere]">
                  {message.content}
                </div>
              ) : (
                <div className="min-w-0 w-full max-w-full overflow-hidden rounded-2xl border border-sky-100 bg-white px-4 py-4 shadow-sm">
                  <div className="mb-3 text-xs font-semibold text-light-grey">Draft response</div>
                  <div className="break-words whitespace-pre-wrap text-sm leading-6 text-accent1 [overflow-wrap:anywhere]">
                    {message.content}
                  </div>
                </div>
              )}
            </div>
          ))}
          {children}
          {pendingStatus && <PendingStatusRow status={pendingStatus} />}
        </div>
      )}
    </div>
  );
}
